package com.anjuros.website.redesign.styles

import com.anjuros.website.redesign.theme.RdColor
import com.anjuros.website.redesign.theme.RdFont
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px

/** When the table scrolls sideways, i.e. its frame's data-scroll is set and isn't "none". */
private const val SCROLLS = "[data-scroll]:not([data-scroll='none'])"

/**
 * Around a table that may be wider than the screen (see [com.anjuros.website.redesign.components.RdScrollTable]).
 * While it scrolls sideways, a "swipe" hint shows above it, the right edge fades out with an arrow while columns
 * are still hidden there, and the first column stays put so each row's label is still visible after swiping.
 * Tables give their first-column cells an opaque background, so the other columns slide under it cleanly.
 * The box's corner radius comes from --rd-table-radius, so the fade fits its rounded corner.
 */
val RdTableFrameStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(8.px).css("min-width" to "0") }
    cssRule("$SCROLLS > span") { Modifier.display(DisplayStyle.Flex) }
    cssRule(" > div") { Modifier.position(Position.Relative).css("min-width" to "0") }
    cssRule(" > div::after") {
        Modifier.position(Position.Absolute).color(RdColor.Brand)
            .css(
                "content" to "\"→\"", "top" to "1px", "bottom" to "1px", "right" to "1px", "width" to "30px",
                "display" to "grid", "align-items" to "center", "justify-items" to "end", "padding-right" to "6px",
                "font" to "600 1.1rem/1 ${RdFont.MONO_STACK}",
                "border-radius" to "0 calc(var(--rd-table-radius, 14px) - 1px) calc(var(--rd-table-radius, 14px) - 1px) 0",
                "background" to "linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,.92) 75%)",
                "pointer-events" to "none", "opacity" to "0", "transition" to "opacity .2s ease",
            )
    }
    cssRule(":is([data-scroll='start'], [data-scroll='middle']) > div::after") { Modifier.css("opacity" to "1") }
    // Cells that span the whole row (group headings) can't stay put, so only single cells are pinned.
    cssRule("$SCROLLS :is(th, td):first-child:not([colspan])") {
        Modifier.position(Position.Sticky).css("left" to "0", "z-index" to "1", "transition" to "box-shadow .2s ease")
    }
    // Once the other columns slide under it, a line and soft shadow keep the fixed column clearly separate.
    cssRule(":is([data-scroll='middle'], [data-scroll='end']) :is(th, td):first-child:not([colspan])") {
        Modifier.css("box-shadow" to "inset -1px 0 0 rgb(220,227,236), 6px 0 10px -6px rgba(30,40,52,.25)")
    }
}

/** "Swipe to see the whole table", hidden until the frame says the table scrolls. */
val RdTableHintStyle = CssStyle.base {
    Modifier.display(DisplayStyle.None).color(RdColor.Muted)
        .css("align-items" to "center", "gap" to "6px", "font" to "500 .72rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".1em", "text-transform" to "uppercase")
}
